/**
 * Homeowner view of their submitted issues.
 * Shows category, submitted date, and status for issues where source='customer'.
 */

import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDistanceToNow } from 'date-fns';
import { ImageIcon } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

const STATUS_DISPLAY: Record<string, { label: string; className: string }> = {
  detected: { label: 'Submitted', className: 'bg-blue-500/15 text-blue-500 border-blue-500/30' },
  reviewed: { label: 'Under review', className: 'bg-wt-amber-primary/15 text-wt-amber-primary border-wt-amber-primary/30' },
  scheduled: { label: 'Service scheduled', className: 'bg-wt-amber-primary/15 text-wt-amber-primary border-wt-amber-primary/30' },
  in_progress: { label: 'In progress', className: 'bg-blue-500/15 text-blue-500 border-blue-500/30' },
  resolved: { label: 'Resolved', className: 'bg-wt-green-primary/15 text-wt-green-primary border-wt-green-primary/30' },
  dismissed: { label: 'Closed', className: '' },
};

/** Homeowner-readable category labels. */
const CATEGORY_LABELS: Record<string, string> = {
  power_went_out: 'Power went out',
  generator_sounds_wrong: 'Generator sounds wrong',
  inverter_error: 'Inverter error',
  storm_damage: 'Storm damage',
  roof_leak: 'Roof leak',
  low_production: 'Low production',
  app_says_offline: 'App says offline',
  battery_not_charging: 'Battery not charging',
  other: 'Other',
};

interface MyIssue {
  id: string;
  title: string;
  homeowner_reported_category: string | null;
  status: string;
  created_at: string;
  photo_paths: string[];
}

interface MyReportedIssuesProps {
  propertyId: string;
}

export function MyReportedIssues({ propertyId }: MyReportedIssuesProps) {
  const { data: issues, isLoading } = useQuery({
    queryKey: ['my-reported-issues', propertyId],
    queryFn: async (): Promise<MyIssue[]> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('service_issues')
        .select('id, title, homeowner_reported_category, status, created_at, photo_paths')
        .eq('property_id', propertyId)
        .eq('source', 'customer')
        .eq('created_by', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      return (data ?? []) as MyIssue[];
    },
    enabled: !!propertyId,
    staleTime: 2 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (!issues || issues.length === 0) {
    return null; // Don't show section if no issues
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
        Your reported issues
      </p>
      {issues.map((issue) => {
        const statusCfg = STATUS_DISPLAY[issue.status] ?? STATUS_DISPLAY.dismissed;
        const catLabel = issue.homeowner_reported_category
          ? CATEGORY_LABELS[issue.homeowner_reported_category] ?? issue.homeowner_reported_category
          : issue.title;

        return (
          <div
            key={issue.id}
            className="flex items-center justify-between p-2.5 rounded-md border border-border/50 bg-wt-bg-tertiary/20"
          >
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="text-sm font-medium truncate">{catLabel}</p>
                {issue.photo_paths?.length > 0 && (
                  <span className="flex items-center gap-0.5 text-muted-foreground shrink-0" title={`${issue.photo_paths.length} photo(s)`}>
                    <ImageIcon className="w-3 h-3" />
                    <span className="text-[10px]">{issue.photo_paths.length}</span>
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(issue.created_at), { addSuffix: true })}
              </p>
            </div>
            <Badge
              variant="outline"
              className={cn('text-[10px] shrink-0 ml-2', statusCfg.className)}
            >
              {statusCfg.label}
            </Badge>
          </div>
        );
      })}
    </div>
  );
}
